import { 
  Box, 
  Button, 
  Checkbox, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  FormHelperText, 
  Grid, 
  InputAdornment, 
  InputLabel, 
  MenuItem, 
  Radio, 
  RadioGroup, 
  Select, 
  TextField, 
  Typography 
} from "@mui/material"
import { LenderInfo, OrderRequestFormInputs, PartyFields, PartyGroups, PersonInfo } from "../../../types/orderRequest"
import { useState, useRef, forwardRef } from 'react'
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form"
import { AddCircleOutline } from "@mui/icons-material"

export const ORDER_TYPES = [{FriendlyName: 'Purchase', SoftProName: 'Purchase'}, {FriendlyName: 'Refinance', SoftProName: 'Refinance'}, {FriendlyName: 'Equity Loan / HELOC', SoftProName: 'Equity'}, {FriendlyName: 'Other', SoftProName: 'Other'}]
export const AGENT_ROLES = ["Buyer's Agent", "Seller's Agent", 'Attorney', 'Lender', 'Other'] as const
export const BUYSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Buyer's Agent", "Lender"]
export const SELLSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Seller's Agent"]
export const NEEDSSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Attorney", "Other"]
export const ORGANIZATION_TYPES = ['Trust', 'Corporation', 'Limited Liability Corp.', 'Limited Liability Company', 'Limited Partnership', 'Partnership', 'Estate'] as const
export const PROPERTY_TYPES = ['Single Family', '1-4 Family', 'PUD', 'Condo', 'Cooperative', 'Unimproved', 'Vacant Land', 'Other']
export const FINANCING_TYPES = ['Conventional', 'FHA', 'VA']
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
export const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf']
export const ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join()

export const EMAIL_PATTERN = {
  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  message: 'Please enter a valid email address',
}

export const PHONE_PATTERN = {
  value: /^(\+|\s|\d){0,3}(\(|-|\s)?\d{3}(\)|-|\s){0,2}\d{3}(-|\s)?\d{4,5}$/,
  message: 'Please enter a valid phone number.',
}

export const ZIP_PATTERN = {
  value: /^\d{5}(-\d{4})?$/,
  message: 'Please enter a valid zip code',
}

export const LOAN_AMOUNT_PATTERN = {
  value: /^\d+(\.\d{1,2})?$/,
  message: 'Please enter a valid amount',
}

const defaultPerson: PersonInfo = {
  FirstName: '',
  MiddleName: '',
  LastName: '',
  Email: '',
  Phone: '',
}

const defaultAddress: PartyFields['Address'] = {
  SameAsProperty: false,
  Address1: '',
  Address2: '',
  City: '',
  State: '',
  Zip: '',
}

export const defaultParties: PartyGroups = {
  Buyer: [
    {
      Type: 'individual',
      OrganizationName: '',
      OrganizationType: '',
      Primary: { ...defaultPerson },
      Spouse: { ...defaultPerson },
      OrgPeople: [{ ...defaultPerson }],
      Address: { ...defaultAddress },
    }
  ],
  Seller: [
    {
      Type: 'individual',
      OrganizationName: '',
      OrganizationType: '',
      Primary: { ...defaultPerson },
      Spouse: { ...defaultPerson },
      OrgPeople: [{ ...defaultPerson }],
      Address: { ...defaultAddress },
    }
  ]
}

export const defaultLenders: LenderInfo[] = [
  {
    LenderName: '',
    LoanAmount: '',
    LoanNumber: '',
    FinancingType: '',
    LenderFirstName: '',
    LenderLastName: '',
    LenderEmail: '',
    LenderPhone: ''
  }
]

export const textFieldProps = {
  variant: 'outlined' as const,
  fullWidth: true,
  sx: { '& .MuiOutlinedInput-root': { backgroundColor: 'white' } },
  margin: 'normal' as const,
}

export const selectFieldProps = {
  sx: { backgroundColor: 'white' },
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 300, // Limit the visual length in pixels
      },
    },
  }
}

/** Spread onto any <Checkbox> to allow Enter to toggle it without submitting the form */
export function checkboxEnterKeyDown(field: { value: boolean | undefined | null; onChange: (v: boolean) => void }) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      field.onChange(!field.value)
    }
  }
}

export const OpenOnFocusSelect = forwardRef<HTMLDivElement, React.ComponentProps<typeof Select>>(
  function OpenOnFocusSelect({ children, onFocus, ...props }, ref) {
    const [open, setOpen] = useState(false)
    const justClosedRef = useRef(false)
    return (
      <Select
        {...props}
        ref={ref}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          justClosedRef.current = true
          setOpen(false)
          // Reset after the focus event that follows menu close has fired
          setTimeout(() => { justClosedRef.current = false }, 0)
        }}
        onFocus={(e) => {
          if (!justClosedRef.current) setOpen(true)
          onFocus?.(e as any)
        }}
      >
        {children}
      </Select>
    )
  }
)

export function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="h6">
      {title}
    </Typography>
  )
}

export function StateSelect({
  name,
  control,
  required,
}: {
  name: any
  control: Control<OrderRequestFormInputs>
  required?: boolean
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: 'State is required' } : undefined}
      render={({ field, fieldState }) => (
        <FormControl fullWidth margin="normal" required={required} error={!!fieldState.error}>
          <InputLabel>State</InputLabel>
          <OpenOnFocusSelect {...field} {...selectFieldProps} label="State">
            <MenuItem value=""><em>Select a State</em></MenuItem>
            {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </OpenOnFocusSelect>
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

export function PersonFieldGroup({
  prefix,
  control,
  required,
}: {
  prefix: string
  control: Control<OrderRequestFormInputs>
  required?: boolean
}) {
  return (
    <>
      {/* Person Contact Info */}
      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.FirstName` as any}
            control={control}
            rules={required ? { required: 'First Name is required' } : undefined}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="First Name" required={required} error={!!required && !!fieldState.error} helperText={required ? fieldState.error?.message : undefined} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.MiddleName` as any}
            control={control}
            render={({ field }) => <TextField {...field} {...textFieldProps} label="Middle Name" />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.LastName` as any}
            control={control}
            rules={required ? { required: 'Last Name is required' } : undefined}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Last Name" required={required} error={!!required && !!fieldState.error} helperText={required ? fieldState.error?.message : undefined} />
            )}
          />
        </Grid>
      </Grid>

      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={`${prefix}.Email` as any}
            control={control}
            rules={{ pattern: EMAIL_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Email" type="email" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={`${prefix}.Phone` as any}
            control={control}
            rules={{ pattern: PHONE_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Phone" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
      </Grid>
    </>
  )
}

function PartyAddressSection({
  contactType,
  partyIndex,
  control,
}: {
  contactType: 'Buyer' | 'Seller'
  partyIndex: number
  control: Control<OrderRequestFormInputs>
}) {
  const prefix = `Parties.${contactType}.${partyIndex}.Address`
  const sameAsProperty = useWatch({ control, name: `${prefix}.SameAsProperty` as any })
  const [watchAddr1, watchAddr2, watchCity, watchState, watchZip] = useWatch({
    control,
    name: [
      `${prefix}.Address1` as any,
      `${prefix}.Address2` as any,
      `${prefix}.City` as any,
      `${prefix}.State` as any,
      `${prefix}.Zip` as any,
    ],
  })
  const anyFilled = !sameAsProperty && !!(watchAddr1 || watchAddr2 || watchCity || watchState || watchZip)

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Controller
        name={`${prefix}.SameAsProperty` as any}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
            label="Contact Address same as Property Address"
          />
        )}
      />
      {!sameAsProperty && (
        <>
          <Typography variant="caption" color="text.secondary">Contact Address</Typography>
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name={`${prefix}.Address1` as any}
                control={control}
                rules={{ validate: (v) => !anyFilled || !!v || 'Address 1 is required' }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Address 1" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name={`${prefix}.Address2` as any}
                control={control}
                render={({ field }) => <TextField {...field} {...textFieldProps} label="Address 2" />}
              />
            </Grid>
          </Grid>
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} sm={12} md={4}>
              <Controller
                name={`${prefix}.City` as any}
                control={control}
                rules={{ validate: (v) => !anyFilled || !!v || 'City is required' }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="City" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <StateSelect name={`${prefix}.State`} control={control} required={anyFilled} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Controller
                name={`${prefix}.Zip` as any}
                control={control}
                rules={{ validate: (v) => !anyFilled || !!v || 'Zip is required', pattern: ZIP_PATTERN }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Zip" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
        </>
      )}
    </Box>
  )
}

function OrgPeopleFieldArray({
  contactType,
  partyIndex,
  control,
}: {
  contactType: 'Buyer' | 'Seller'
  partyIndex: number
  control: Control<OrderRequestFormInputs>
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `Parties.${contactType}.${partyIndex}.OrgPeople` as any,
  })

  return (
    <>
      {fields.map((field, i) => (
        <Box key={field.id}>
          {fields.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Contact Person {i + 1}</Typography>
              <Button size="small" color="error" type="button" onClick={() => remove(i)}>Remove</Button>
            </Box>
          )}
          <PersonFieldGroup
            prefix={`Parties.${contactType}.${partyIndex}.OrgPeople.${i}`}
            control={control}
            required={i === 0}
          />
          {i < fields.length - 1 && <Divider sx={{ my: 1.5 }} />}
        </Box>
      ))}
      <Button
        startIcon={<AddCircleOutline />}
        size="small"
        variant="text"
        type="button"
        onClick={() => append({ ...defaultPerson })}
        sx={{ mt: 0.5 }}
      >
        Add Contact Person
      </Button>
    </>
  )
}

export function PartySection({
  contactType,
  index,
  control,
  required,
  onRemove,
}: {
  contactType: 'Buyer'|'Seller'
  index: number
  control: Control<OrderRequestFormInputs>
  required?: boolean
  onRemove?: () => void
}) {
  const partyType = useWatch({ control, name: `Parties.${contactType}.${index}.Type` })
  const isMarried = partyType === 'married'
  const isOrganization = partyType === 'organization'
  const isSectionRequired = !!required

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{contactType === 'Buyer' ? 'Buyer/Borrower' : 'Seller'} {index + 1}</Typography>
        <Button size="small" color="error" type="button" onClick={onRemove} sx={{ visibility: onRemove ? 'visible' : 'hidden' }}>Remove</Button>
      </Box>

      <Controller
        name={`Parties.${contactType}.${index}.Type`}
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <RadioGroup row {...field}>
              <FormControlLabel value="individual" control={<Radio size="small" />} label="Individual" />
              <FormControlLabel value="married" control={<Radio size="small" />} label="Married" />
              <FormControlLabel value="organization" control={<Radio size="small" />} label="Organization" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {isOrganization && (
        <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name={`Parties.${contactType}.${index}.OrganizationName`}
              control={control}
              rules={isSectionRequired ? { required: 'Organization name is required' } : undefined}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Organization Name" required={isSectionRequired} error={isSectionRequired && !!fieldState.error} helperText={isSectionRequired ? fieldState.error?.message : undefined} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller 
              name={`Parties.${contactType}.${index}.OrganizationType`} 
              control={control}
              rules={isSectionRequired ? { required: 'Organization type is required' } : undefined}
              render={({ field, fieldState }) => (
                <FormControl required={isSectionRequired} error={isSectionRequired && !!fieldState.error} fullWidth margin="normal">
                  <InputLabel>Type of Organization</InputLabel>
                  <OpenOnFocusSelect {...field} {...selectFieldProps} label="Type of Organization">
                    {ORGANIZATION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </OpenOnFocusSelect>
                  {isSectionRequired && fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      )}

      <PartyAddressSection contactType={contactType} partyIndex={index} control={control} />

      {isMarried && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Primary Borrower
        </Typography>
      )}
      {isOrganization ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Contact Person(s)
          </Typography>
          <OrgPeopleFieldArray contactType={contactType} partyIndex={index} control={control} />
        </>
      ) : (
        <PersonFieldGroup prefix={`Parties.${contactType}.${index}.Primary`} control={control} required={isSectionRequired} />
      )}

      {isMarried && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Spouse
          </Typography>
          <PersonFieldGroup prefix={`Parties.${contactType}.${index}.Spouse`} control={control} required={isSectionRequired} />
        </>
      )}
    </Box>
  )
}

/* Lender Information */
export function LenderSection({
  index,
  control,
  required,
  onRemove,
}: {
  index: number
  control: Control<OrderRequestFormInputs>
  required?: boolean
  onRemove?: () => void
}) {
  const isRequesterLender = useWatch({control, name: 'AgentRole' as const}) === "Lender"
  const lenderSetToRequester = useWatch({control, name: `Lenders.${index}.SameAsRole` as any})
  const isSectionRequired = !!required
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      {isRequesterLender && <Controller
        name={`Lenders.${index}.SameAsRole` as any}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value ?? false} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
            label="Lender is the Requester for this Title Order"
          />
        )}
      />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{'Lender'} {index + 1}</Typography>
        <Button size="small" color="error" type="button" onClick={onRemove} sx={{ visibility: onRemove ? 'visible' : 'hidden' }}>Remove</Button>
      </Box>

      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        {!lenderSetToRequester && <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LenderName`} control={control}
            render={({ field }) => <TextField {...field} {...textFieldProps} label="New Lender Name" />}
          />
        </Grid>}
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LoanAmount`} control={control} rules={isSectionRequired ? { pattern: LOAN_AMOUNT_PATTERN, required: 'Loan amount is required' } : { pattern: LOAN_AMOUNT_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Loan Amount" required={isSectionRequired}
                error={!!fieldState.error && (isSectionRequired || fieldState.error.type !== 'required')} helperText={(isSectionRequired || fieldState.error?.type !== 'required') ? fieldState.error?.message : undefined}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LoanNumber`} control={control} rules={isSectionRequired ? { required: 'Loan number is required' } : undefined}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Loan Number" required={isSectionRequired} error={isSectionRequired && !!fieldState.error} helperText={isSectionRequired ? fieldState.error?.message : undefined} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.FinancingType`} control={control} rules={isSectionRequired ? { required: 'Financing type is required' } : undefined}
            render={({ field, fieldState }) => (
              <FormControl fullWidth margin="normal" required={isSectionRequired} error={isSectionRequired && !!fieldState.error}>
                <InputLabel>Financing Type</InputLabel>
                <OpenOnFocusSelect {...field} {...selectFieldProps} label="Financing Type">
                  <MenuItem value=""><em>Select Type</em></MenuItem>
                  {FINANCING_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </OpenOnFocusSelect>
                {isSectionRequired && fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        {!lenderSetToRequester &&
        <>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderFirstName`} control={control}
              render={({ field }) => <TextField {...field} {...textFieldProps} label="Lender First Name" />}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderLastName`} control={control}
              render={({ field }) => <TextField {...field} {...textFieldProps} label="Lender Last Name" />}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderEmail`} control={control} rules={{ pattern: EMAIL_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Lender Email" type="email" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderPhone`} control={control} rules={{ pattern: PHONE_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Lender Phone Number" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </>
        }
      </Grid>
    </Box>  
  )
}
